import React from 'react';
import { PasswordInput } from '../../components/PasswordInput';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colors, fonts, gradients, shadows } from '../../theme/tokens';
import { authLoginStart, authLoginChanged } from '../../redux/auth/auth.action';
import {
  selectAuthStatus,
  selectAuthError,
  selectAuthErrorCode,
  selectIsLoggedIn,
} from '../../redux/auth/auth.selectors';
import { usePrefersReducedMotion } from '../../components/useCountUp';

/* §3.1 Login — parallax hero, green #0C2B1D, animated mesh, depth layers on
   mouse-move, floating sign-in card with 3D tilt. */
export const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduced = usePrefersReducedMotion();
  const motionOn = !reduced;

  const authStatus = useSelector(selectAuthStatus);
  const authError = useSelector(selectAuthError);
  // A 429 means "wait", not "wrong password" — it gets its own colour.
  const waiting = useSelector(selectAuthErrorCode) === 'RATE_LIMITED';
  const isLoggedIn = useSelector(selectIsLoggedIn);

  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const canSubmit = username.trim() !== '' && password !== '' && !submitting;
  const [mouse, setMouse] = React.useState({ dx: 0, dy: 0 });

  // On a successful login the auth slice flips isLoggedIn → route to dashboard.
  React.useEffect(() => {
    if (isLoggedIn) navigate('/dashboard', { replace: true });
  }, [isLoggedIn, navigate]);

  // Stop the spinner once the saga resolves (Success handled above, Failure shows error).
  React.useEffect(() => {
    if (authStatus === 'Failure') setSubmitting(false);
  }, [authStatus]);

  const onMouseMove = (e: React.MouseEvent): void => {
    if (!motionOn) return;
    const dx = e.clientX / window.innerWidth - 0.5;
    const dy = e.clientY / window.innerHeight - 0.5;
    setMouse({ dx, dy });
  };
  const onMouseLeave = (): void => setMouse({ dx: 0, dy: 0 });

  const signIn = (e?: React.FormEvent): void => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    if (authStatus === 'Failure') dispatch(authLoginChanged()); // clear stale error
    dispatch(authLoginStart({ username, password }));
  };

  const { dx, dy } = mouse;

  return (
    <div
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{
        minHeight: '100vh',
        overflow: 'hidden',
        background: colors.loginBg,
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
        fontFamily: fonts.body,
      }}
    >
      {/* Mesh layer */}
      <div
        style={{
          position: 'absolute',
          inset: '-20%',
          background: gradients.loginMesh,
          animation: motionOn ? 'meshShift 18s infinite' : undefined,
          transform: `translate(${dx * 10}px, ${dy * 8}px)`,
        }}
      />
      {/* Parallax layer 1 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform .25s',
          transform: `translate(${dx * -18}px, ${dy * -12}px)`,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '-60px',
            left: '-40px',
            width: 320,
            height: 320,
            borderRadius: 99,
            border: '1px solid rgba(184,134,11,.35)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '-120px',
            right: '-80px',
            width: 420,
            height: 420,
            borderRadius: 99,
            border: '1px solid rgba(247,244,236,.12)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '40%',
            left: '18%',
            width: 120,
            height: 120,
            borderRadius: 28,
            background: 'rgba(184,134,11,.10)',
            transform: 'rotate(18deg)',
          }}
        />
      </div>
      {/* Parallax layer 2 (floating shapes) */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          transition: 'transform .25s',
          transform: `translate(${dx * -34}px, ${dy * -24}px)`,
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '22%',
            right: '26%',
            width: 64,
            height: 64,
            borderRadius: 18,
            background: 'rgba(247,244,236,.06)',
            animation: motionOn ? 'floatA 7s ease-in-out infinite' : undefined,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '26%',
            left: '30%',
            width: 44,
            height: 44,
            borderRadius: 99,
            background: 'rgba(184,134,11,.28)',
            animation: motionOn ? 'floatA 9s ease-in-out infinite' : undefined,
          }}
        />
        <img
          src="/tnsc-logo.jpg"
          alt=""
          style={{
            position: 'absolute',
            top: '30%',
            right: '12%',
            width: 88,
            opacity: 0.14,
            borderRadius: 18,
            animation: motionOn ? 'floatA 11s ease-in-out infinite' : undefined,
          }}
        />
      </div>

      {/* Content grid */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          width: 'min(1060px, 94vw)',
          display: 'grid',
          gridTemplateColumns: '1.1fr .9fr',
          gap: 56,
          alignItems: 'center',
        }}
      >
        {/* Left — brand */}
        <div style={{ color: colors.cream }}>
          <img
            src="/tnsc-logo.jpg"
            alt="TNSC"
            width={72}
            height={72}
            style={{ borderRadius: 18, objectFit: 'cover' }}
          />
          <div
            style={{
              fontSize: 13,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: colors.goldLight,
              marginTop: 18,
            }}
          >
            The Tamil Nadu State Apex Cooperative Bank
          </div>
          <div style={{ fontFamily: fonts.serif, fontSize: 34, marginTop: 4 }}>TISMS</div>
          <div style={{ fontFamily: fonts.serif, fontSize: 44, marginTop: 12, lineHeight: 1.15 }}>
            Inspection &amp; Supervisory Monitoring System
          </div>
          <p
            style={{
              fontSize: 16,
              color: 'rgba(247,244,236,.75)',
              maxWidth: '46ch',
              marginTop: 16,
              lineHeight: 1.5,
            }}
          >
            Which DCCB is risky · why · who fixes it · has it been fixed — a single supervisory
            surface for the apex bank, its DCCBs and their branches.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            <Chip text="E-CAMELSC aligned" border="rgba(184,134,11,.5)" color={colors.goldLighter} />
            <Chip
              text="BR REPORT-FORMAT digitised"
              border="rgba(184,134,11,.5)"
              color={colors.goldLighter}
            />
            <Chip text="NABARD CSITE" border="rgba(247,244,236,.25)" color={colors.cream} />
          </div>
        </div>

        {/* Right — sign-in card (3D tilt) */}
        <div
          style={{
            background: 'rgba(247,244,236,.97)',
            borderRadius: 18,
            padding: 28,
            boxShadow: shadows.loginCard,
            transition: 'transform .2s',
            willChange: 'transform',
            transform: motionOn
              ? `perspective(900px) rotateX(${dy * -4}deg) rotateY(${dx * 5}deg) translateY(-3px)`
              : undefined,
          }}
        >
          <div style={{ fontFamily: fonts.serif, fontSize: 22, color: colors.brandGreen }}>
            Sign in
          </div>
          <div style={{ fontSize: 13, color: colors.muted, marginTop: 2 }}>
            Use the credentials issued to you — உள்நுழைய உங்கள் அடையாளச் சான்று
          </div>

          <form onSubmit={signIn} style={{ marginTop: 14 }}>
            <input
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (authStatus === 'Failure') dispatch(authLoginChanged());
              }}
              placeholder="username@tnscbank.in"
              autoComplete="username"
              autoFocus
              style={inputStyle}
            />
            <PasswordInput
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (authStatus === 'Failure') dispatch(authLoginChanged());
              }}
              placeholder="Password"
              autoComplete="current-password"
              style={{ ...inputStyle, marginTop: 8 }}
            />
            {/* a hidden submit so Enter in either field signs in */}
            <button type="submit" style={{ display: 'none' }} aria-hidden />
          </form>

          {/* Bad credentials, a network failure — or a wait, which is neither. */}
          {authStatus === 'Failure' && authError && (
            <div
              style={{
                marginTop: 12,
                background: waiting ? '#FBF3E0' : '#FDF3F2',
                border: `1px solid ${waiting ? '#E0C98A' : '#ECC7C3'}`,
                color: waiting ? '#7A5B16' : colors.danger2,
                borderRadius: 8,
                padding: '9px 12px',
                fontSize: 12.5,
              }}
            >
              {authError}
            </div>
          )}

          {/* Sign-in button */}
          <button
            onClick={() => signIn()}
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: 13,
              borderRadius: 10,
              fontWeight: 600,
              border: 'none',
              marginTop: 14,
              color: colors.cream,
              background: canSubmit ? colors.brandGreen : '#9AA5A0',
              boxShadow: canSubmit ? shadows.primaryBtn : undefined,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
            }}
          >
            {submitting && (
              <span
                style={{
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  border: '2px solid rgba(247,244,236,.4)',
                  borderTopColor: colors.cream,
                  display: 'inline-block',
                  animation: 'spin 0.7s linear infinite',
                }}
              />
            )}
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div style={{ fontSize: 11, color: colors.faint, textAlign: 'center', marginTop: 12 }}>
            Confidential — Internal &amp; Regulatory use · MFA enforced for privileged roles
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  borderRadius: 8,
  border: `1px solid ${colors.borderWarm}`,
  padding: '11px 12px',
  fontSize: 13,
  marginTop: 12,
  background: '#fff',
  color: colors.ink,
};

const Chip: React.FC<{ text: string; border: string; color: string }> = ({ text, border, color }) => (
  <span
    style={{
      borderRadius: 99,
      fontSize: 12,
      padding: '6px 12px',
      border: `1px solid ${border}`,
      color,
    }}
  >
    {text}
  </span>
);


export default LoginPage;
